/* RESPONSIBLE TEAM: team-self-serve */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type CompellingOfferCampaignService from 'embercom/services/compelling-offer-campaign-service';
import type CardlessConversionModalService from 'embercom/services/cardless-conversion-modal-service';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import moment from 'moment-timezone';

const MODAL_TRIGGER_LOOKUP_KEYS = ['campaign_trial_reengagement'];

export default class CompellingOfferClaimModalService extends Service {
  @service declare compellingOfferCampaignService: CompellingOfferCampaignService;
  @service declare cardlessConversionModalService: CardlessConversionModalService;
  @service declare cardlessTrialService: CardlessTrialService;

  @tracked isOpen = false;
  private hasOpened = false;

  maybeOpen(): void {
    if (this.hasOpened) {
      return;
    }

    let campaign = this.compellingOfferCampaignService.redeemableCampaign;
    if (
      !campaign ||
      campaign.campaign_type !== 'activation' ||
      !MODAL_TRIGGER_LOOKUP_KEYS.includes(campaign.lookup_key) ||
      !this.compellingOfferCampaignService.isEligibleForCampaignExperience ||
      this.isExpired(campaign.redemption_expires_at)
    ) {
      return;
    }

    this.hasOpened = true;
    this.isOpen = true;
  }

  @action onClaim(): void {
    this.isOpen = false;
    this.cardlessConversionModalService.toggle();
  }

  @action onSkip(): void {
    this.isOpen = false;
  }

  get badge(): string {
    return this.compellingOfferCampaignService.campaignTranslations.claimModalBadge;
  }

  get headline(): string {
    return this.compellingOfferCampaignService.campaignTranslations.claimModalHeadline;
  }

  get body(): string {
    let expiryDate = this.compellingOfferCampaignService.redeemableCampaign?.redemption_expires_at
      ? moment(
          this.compellingOfferCampaignService.redeemableCampaign?.redemption_expires_at,
        ).format('D MMM, YYYY')
      : '';
    return this.compellingOfferCampaignService.campaignTranslations.claimModalBody(expiryDate);
  }

  get claimCta(): string {
    return this.compellingOfferCampaignService.campaignTranslations.claimModalClaimCta;
  }

  get skipCta(): string {
    return this.compellingOfferCampaignService.campaignTranslations.claimModalSkipCta;
  }

  private isExpired(date: string | null | undefined): boolean {
    if (!date) {
      return false;
    }
    return new Date(date) < new Date();
  }
}

declare module '@ember/service' {
  interface Registry {
    compellingOfferClaimModalService: CompellingOfferClaimModalService;
    'compelling-offer-claim-modal-service': CompellingOfferClaimModalService;
  }
}
